import { useMemo } from "react";
import { IconSun, IconShadow, IconCoffee, IconGlass, IconToolsKitchen2 } from "@tabler/icons-react";
import type { CafePointFeature } from "../lib/types";
import { getOpeningHoursStatus } from "../lib/opening-hours";
import type { SelectedCafe } from "./MapView";
import type { CategoryValue, SunValue } from "./CategoryFilter";

type PlaceSuggestionsProps = {
  places: CafePointFeature[];
  selectedCategories: Set<CategoryValue>;
  selectedSun: Set<SunValue>;
  openOnly: boolean;
  simulatedDate: Date;
  onPlaceClick: (cafe: SelectedCafe) => void;
};

function filterPlaces(
  places: CafePointFeature[],
  selectedCategories: Set<CategoryValue>,
  selectedSun: Set<SunValue>,
  openOnly: boolean,
  simulatedDate: Date,
): CafePointFeature[] {
  if (selectedCategories.size === 0 || selectedSun.size === 0) {
    return [];
  }

  return places.filter(f => {
    if (!selectedCategories.has(f.properties.category)) return false;
    const inSun = f.properties.inShadow !== true;
    const matchesSun =
      (selectedSun.has("sun") && inSun) ||
      (selectedSun.has("shade") && !inSun);
    if (!matchesSun) return false;
    if (openOnly) {
      const status = getOpeningHoursStatus(f.properties.openingHours, simulatedDate, {
        coordinates: f.geometry.coordinates as [number, number],
        countryCode: f.properties.countryCode,
        state: f.properties.state,
      });
      if (status.state !== "open") return false;
    }
    return true;
  });
}

function toSelectedCafe(feature: CafePointFeature): SelectedCafe {
  const p = feature.properties;
  const coords = feature.geometry.coordinates as [number, number];
  return {
    name:
      p.name ??
      (p.category === "bar"
        ? "Unnamed bar"
        : p.category === "restaurant"
          ? "Unnamed restaurant"
          : "Unnamed cafe"),
    address: p.address ?? "",
    openingHours: p.openingHours ?? "",
    outdoorSeating: p.outdoorSeating ?? "unknown",
    category: p.category,
    countryCode: p.countryCode,
    state: p.state,
    osmType: p.osmType,
    osmId: String(p.osmId),
    coordinates: coords,
    inShadow: p.inShadow,
  };
}

export function PlaceSuggestions({
  places,
  selectedCategories,
  selectedSun,
  openOnly,
  simulatedDate,
  onPlaceClick,
}: PlaceSuggestionsProps) {
  const filtered = useMemo(
    () =>
      filterPlaces(places, selectedCategories, selectedSun, openOnly, simulatedDate)
        .filter(f => f.properties.name)
        .sort((a, b) => (a.properties.name ?? "").localeCompare(b.properties.name ?? "")),
    [places, selectedCategories, selectedSun, openOnly, simulatedDate],
  );

  if (places.length === 0) {
    return (
      <p className="mt-3 text-center text-xs text-slate-400">
        Zoom in closer to see places.
      </p>
    );
  }

  if (filtered.length === 0) {
    return (
      <p className="mt-3 text-center text-xs text-slate-400">
        No matching places found.
      </p>
    );
  }

  return (
    <ul className="mt-3 max-h-36 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 sm:max-h-52">
      {filtered.map(feature => {
        const p = feature.properties;
        const isSun = p.inShadow !== true;
        return (
          <li key={`${p.osmType}-${p.osmId}`}>
            <button
              type="button"
              onClick={() => onPlaceClick(toSelectedCafe(feature))}
              className="flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-slate-50"
            >
              <div className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-800">
                  {p.name}
                </span>
                {p.address ? (
                  <span className="block truncate text-xs text-slate-400">
                    {p.address}
                  </span>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1 pt-0.5">
                {p.category === "bar" ? (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                    <IconGlass size={10} stroke={2} />
                    Bar
                  </span>
                ) : p.category === "restaurant" ? (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                    <IconToolsKitchen2 size={10} stroke={2} />
                    Restaurant
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                    <IconCoffee size={10} stroke={2} />
                    Cafe
                  </span>
                )}
                {isSun ? (
                  <span className="inline-flex items-center rounded-full bg-amber-50 p-0.5 text-amber-500">
                    <IconSun size={12} stroke={2} />
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-slate-100 p-0.5 text-slate-400">
                    <IconShadow size={12} stroke={2} />
                  </span>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
